import { useEffect, lazy, Suspense, type ComponentType } from 'react';
import { Switch, Route, useLocation, useParams, Redirect } from 'wouter';
import { SWRConfig } from 'swr';
import { queryClient } from './lib/queryClient';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ThemeProvider } from '@/components/theme-provider';
import { LoadingScreen } from '@/components/LoadingScreen';
import { useAuth } from '@/hooks/useAuth';
import { useHostnameContext } from '@/hooks/useHostnameContext';
import { useQuery } from '@tanstack/react-query';
import { useWebSocket, pathNeedsLiveSocket } from '@/lib/socket';
import { MobileAppShell } from '@/components/layout/mobile-app-shell';

// --- Sync only error pages (reliability). Everything else is code-split. ---
import NotFoundPage from '@/pages/error-pages/404';
import UnauthorizedPage from '@/pages/error-pages/401';
import ServerErrorPage from '@/pages/error-pages/500';

import { ErrorBoundary } from '@/components/error-boundary';
import { setSentryUser, setSentryTags } from '@/lib/sentry';
import {
  setPosthogUser,
  setCoachIdentity,
  clearIdentity,
  startCoachRecording,
  stopCoachRecording,
  track,
} from '@/lib/posthog';
import { useCoachSession } from '@/hooks/use-coach-session';
import { ErrorFallback } from '@/components/ErrorFallback';
import { RateLimitListener } from '@/components/RateLimitListener';
import { RequireAuth } from '@/components/auth/RequireAuth';
import { withLeagueAdminShell } from '@/components/league/LeagueAdminShell';
import { lazyWithRetry } from '@/lib/lazyWithRetry';

// --- Lazy Imports (keeps auth/marketing off the main entry chunk) ---
const Home = lazyWithRetry(() => import('@/pages/home'));
const LandingPageBroadcast = lazyWithRetry(() => import('@/pages/LandingPageBroadcast'));
const PublicDirectory = lazy(() => import('@/pages/public-directory'));
const LoginPage = lazy(() => import('@/pages/login'));
const RegisterPage = lazy(() => import('@/pages/register'));
const ForgotPasswordPage = lazy(() => import('@/pages/forgot-password'));
const ResetPasswordPage = lazy(() => import('@/pages/reset-password'));
const VerifyEmailPage = lazy(() => import('@/pages/verify-email'));
const VerifyMagicLinkPage = lazy(() => import('@/pages/verify-magic-link'));
const OrganizationPage = lazy(() => import('@/pages/organization'));
const Dashboard = lazy(() => import('@/pages/dashboard'));
const CoachScoreInput = lazy(() => import('@/pages/coach-score-input'));
const AdminPortal = lazy(() => import('@/pages/admin-portal'));
const TournamentDashboard = lazy(() => import('@/pages/tournament-dashboard'));
const TournamentStatusBoardPage = lazy(() => import('@/pages/tournament-status-board'));
const ValidationReport = lazy(() => import('@/pages/validation-report'));
const MagicLoginPage = lazy(() => import('@/pages/magic'));
const CoachDashboard = lazy(() => import('@/pages/coach-dashboard'));
const CoachDashboardDemo = lazy(() => import('@/pages/coach-dashboard-demo'));
const StaffLeavePage = lazy(() => import('@/pages/staff-leave'));
const StaffingBoardPage = lazy(() => import('@/pages/staffing-board'));
const DemoHub = lazy(() => import('@/pages/demo-hub'));
const TournamentRegistrationComingSoon = lazy(
  () => import('@/pages/coming-soon/tournament-registration')
);
const TournamentCommsComingSoon = lazy(() => import('@/pages/coming-soon/tournament-comms'));
const ScheduleBuilderComingSoon = lazy(() => import('@/pages/coming-soon/schedule-builder'));
const RegistrationPage = lazy(() => import('@/pages/registration'));
const CampaignRegistrationPage = lazy(() => import('@/pages/registration/campaign'));
const RegistrationStatusPage = lazy(() => import('@/pages/registration/status'));
const DirectorApplicationReviewPage = lazy(() => import('@/pages/registration/director-review'));
const RegistrationSuccessPage = lazy(() => import('@/pages/registration/success'));
const RegistrationReceiptPage = lazy(() => import('@/pages/registration/receipt'));
const StayPortalPage = lazy(() => import('@/pages/stay-portal'));
const LeagueRegistrationPage = lazy(() => import('@/pages/public/league-registration'));
const AdminTournamentDashboardRedirect = lazy(
  () => import('@/pages/admin-tournament-dashboard-redirect')
);
const RosterEntryPage = lazy(() => import('@/pages/roster-entry'));
const BookingHub = lazy(() => import('@/pages/booking/booking-hub'));
const NewBookingRequest = lazy(() => import('@/pages/booking/new-booking-request'));
const BookingRequestDetail = lazy(() => import('@/pages/booking/booking-request-detail'));
const BookingCalendarPage = lazy(() =>
  import('@/pages/booking/booking-calendar-page').then(m => ({ default: m.BookingCalendarPage }))
);
const AdminBookingCalendarPage = lazy(() =>
  import('@/pages/booking/admin-booking-calendar-page').then(m => ({
    default: m.AdminBookingCalendarPage,
  }))
);
const CoordinatorApprovals = lazy(() => import('@/pages/booking/coordinator-approvals'));
const TeamManagementPage = lazy(() => import('@/pages/booking/team-management-page'));
const BookingReportsPage = lazy(() => import('@/pages/booking/booking-reports-page'));
const BookingSettingsPage = lazy(() => import('@/pages/booking/booking-settings-page'));
const TwilioSettings = lazy(() => import('@/pages/TwilioSettings'));
const WeatherSettings = lazy(() => import('@/pages/WeatherSettings'));
const StripeSettings = lazy(() => import('@/pages/StripeSettings'));
const WeatherDashboard = lazy(() => import('@/pages/WeatherDashboard'));
const Communications = lazy(() => import('@/pages/Communications'));
const TournamentCommunications = lazy(() => import('@/pages/TournamentCommunications'));
const InviteAcceptance = lazy(() => import('@/pages/invite-acceptance'));
const AdminInviteAcceptance = lazy(() => import('@/pages/admin-invite-acceptance'));
const TeamUpdate = lazy(() => import('@/pages/TeamUpdate'));
const OrganizationSelector = lazy(() => import('@/pages/organization-selector'));
const WelcomePage = lazy(() => import('@/pages/welcome').then(m => ({ default: m.WelcomePage })));
const OnboardingCreateOrganization = lazy(() => import('@/pages/onboarding-create-organization'));
const FirstTournamentOnboardingPage = lazy(() => import('@/pages/onboarding/first-tournament'));
const TournamentRegistrationShellPage = lazy(() => import('@/pages/tournament-registration-shell'));
const DirectorOnboardingWizardPage = lazy(() => import('@/pages/onboarding/director-wizard'));
const TournamentLaunchPage = lazy(() => import('@/pages/onboarding/tournament-launch'));
const OrgAdminPortal = lazy(() => import('@/pages/OrgAdminPortal'));
const OrgSettings = lazy(() => import('@/pages/OrgSettings'));
const OrgStripeSettingsPage = lazy(() => import('@/pages/org-stripe-settings'));
const OrgFinanceDeskPage = lazy(() => import('@/pages/org/finance-desk'));
const OrgRegistrationsPage = lazy(() => import('@/pages/admin/org-registrations'));
const OrgRegistrationCampaignsPage = lazy(() => import('@/pages/admin/registration-campaigns'));
const UserManagement = lazy(() => import('@/pages/user-management'));
// Platform Admin. Each page is already wrapped in `withPlatformAdminShell`,
// which fetches the session and renders AccessDenied for a non-super-admin, so
// these do not take RequireAuth.
const PlatformDashboardPage = lazy(() => import('@/pages/platform/index'));
const PlatformIntegrationsPage = lazy(() => import('@/pages/platform/integrations'));
const PlatformFeesPage = lazy(() => import('@/pages/platform/fees'));
const PlatformFeaturesPage = lazy(() => import('@/pages/platform/features'));
const PlatformUsersPage = lazy(() => import('@/pages/platform/users'));
const PlatformOrganizationsPage = lazy(() => import('@/pages/platform/organizations'));
const PlatformRequestsPage = lazy(() => import('@/pages/platform/requests'));
const PartnerPortalPage = lazy(() => import('@/pages/partner/index'));
const BlogAdminPage = lazy(() => import('@/pages/admin/blog-editor'));
const LeagueCommandCenter = lazy(() => import('@/pages/admin/league/index'));
const LeagueDraftBoardPage = lazy(() => import('@/pages/admin/league/draft-board-page'));
const LeagueDivisionDetailPage = lazy(() => import('@/pages/admin/league/division-detail'));
const RosterKioskPage = lazy(() => import('@/pages/admin/league/roster-kiosk'));
const LeagueMasterSchedulerPage = lazy(() => import('@/pages/admin/league/master-scheduler'));
const LeagueUniformsPage = lazy(() => import('@/pages/admin/league/uniforms'));
const LeagueUniformManifestPage = lazy(() => import('@/pages/admin/league/uniform-manifest'));
const LeagueCompliancePage = lazy(() => import('@/pages/admin/league/compliance'));
const LeagueFinancialsPage = lazy(() => import('@/pages/admin/league/financials'));
const LeagueAiSchedulerSandboxPage = lazy(
  () => import('@/pages/admin/league/ai-scheduler-sandbox')
);
const ForestGladeStaffPage = lazy(() => import('@/pages/admin/org/forest-glade/staff'));
const FitFormPage = lazy(() => import('@/pages/fit/p/fit-form'));
const CoachFitFormPage = lazy(() => import('@/pages/fit/c/coach-fit-form'));
const CoachLeaguePage = lazy(() => import('@/pages/coach/league/CoachLeaguePage'));
const TermsOfServicePage = lazy(() => import('@/pages/legal/terms'));
const AdminTermsOfServicePage = lazy(() => import('@/pages/legal/terms-admin'));
const TournamentTermsOfServicePage = lazy(() => import('@/pages/legal/terms-tournament'));
const PrivacyPolicyPage = lazy(() => import('@/pages/legal/privacy'));
const PrivacySettingsPage = lazy(() => import('@/pages/settings/privacy'));
const AlgorithmExplanationPage = lazy(() => import('@/pages/legal/algorithm-explanation'));

const CoachTeamView = lazy(() => import('@/pages/coach-team'));

const LeagueCommandCenterWithShell = withLeagueAdminShell(
  LeagueCommandCenter as ComponentType<Record<string, never>>
);
const LeagueDraftBoardWithShell = withLeagueAdminShell(
  LeagueDraftBoardPage as ComponentType<Record<string, never>>
);
const LeagueDivisionDetailWithShell = withLeagueAdminShell(
  LeagueDivisionDetailPage as ComponentType<Record<string, never>>
);
const RosterKioskWithShell = withLeagueAdminShell(
  RosterKioskPage as ComponentType<Record<string, never>>
);
const LeagueMasterSchedulerWithShell = withLeagueAdminShell(
  LeagueMasterSchedulerPage as ComponentType<Record<string, never>>
);
const LeagueUniformsWithShell = withLeagueAdminShell(
  LeagueUniformsPage as ComponentType<Record<string, never>>
);
const LeagueUniformManifestWithShell = withLeagueAdminShell(
  LeagueUniformManifestPage as ComponentType<Record<string, never>>
);
const LeagueComplianceWithShell = withLeagueAdminShell(
  LeagueCompliancePage as ComponentType<Record<string, never>>
);
const LeagueFinancialsWithShell = withLeagueAdminShell(
  LeagueFinancialsPage as ComponentType<Record<string, never>>
);
const LeagueAiSchedulerSandboxWithShell = withLeagueAdminShell(
  LeagueAiSchedulerSandboxPage as ComponentType<Record<string, never>>
);

function BookingRedirect() {
  const params = useParams();
  const orgId = params.orgId;
  useEffect(() => {
    if (orgId) {
      window.location.replace(`/booking/${orgId}/hub`);
    }
  }, [orgId]);
  return null;
}

function HostnameAwareHome() {
  const { isStorefront, isLoading: hostnameLoading } = useHostnameContext();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [location, setLocation] = useLocation();

  // Fetch user data for authenticated users
  const { data: user, isLoading: userLoading } = useQuery<any>({
    queryKey: ['/api/auth/me'],
    enabled: isAuthenticated,
  });

  // Fetch user's organizations for authenticated users
  // Add error handling to prevent infinite loading if fetch fails
  const {
    data: userOrgs,
    isLoading: orgsLoading,
    error: orgsError,
  } = useQuery<Array<any>>({
    queryKey: ['/api/users/me/organizations'],
    enabled: isAuthenticated && !!user && !user.partnerId,
    retry: false, // Don't retry on failure
    // Return empty array on error to allow page to render
    queryFn: async () => {
      try {
        const res = await fetch('/api/users/me/organizations', {
          credentials: 'include',
        });
        if (!res.ok) {
          console.error('Failed to fetch organizations:', res.status, res.statusText);
          return []; // Return empty array on error
        }
        return await res.json();
      } catch (error) {
        console.error('Error fetching organizations:', error);
        return []; // Return empty array on error
      }
    },
  });

  // Hard redirect logic for authenticated users on homepage
  // Check early to prevent flicker - redirect before rendering Home
  useEffect(() => {
    // Only redirect if we have user data and organizations data (or error)
    // Don't wait forever if organizations fetch fails
    if (isAuthenticated && !userLoading && !orgsLoading && user) {
      // If organizations fetch failed, treat as empty array (new user)
      const orgs = orgsError ? [] : userOrgs || [];

      if (user.partnerId) {
        setLocation('/partner');
        return;
      }

      if (user.isSuperAdmin) {
        // Super admin - redirect to org selector
        setLocation('/select-organization');
        return;
      } else if (orgs.length === 0) {
        // New user with no orgs - redirect to /welcome
        setLocation('/welcome');
        return;
      }
    }
  }, [isAuthenticated, userLoading, orgsLoading, user, userOrgs, orgsError, setLocation]);

  // Guests: paint landing immediately — do not block on auth/context.
  // Authenticated users: brief spinner only while resolving redirect targets.
  if (hostnameLoading) {
    return <LandingPageBroadcast />;
  }

  if (isStorefront) {
    return <PublicDirectory />;
  }

  if (!isAuthenticated && !authLoading) {
    return <LandingPageBroadcast />;
  }

  // Auth still resolving — show landing (progressive enhancement) instead of a spinner
  if (authLoading && !isAuthenticated) {
    return <LandingPageBroadcast />;
  }

  if (isAuthenticated && (userLoading || (orgsLoading && !orgsError))) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Early return for users who should be redirected (prevent Home render)
  if (isAuthenticated && user) {
    const orgs = orgsError ? [] : userOrgs || [];
    if (user.partnerId || user.isSuperAdmin) {
      return <PublicDirectory />; // Will redirect via useEffect
    } else if (orgs.length === 0) {
      return <PublicDirectory />; // Will redirect via useEffect
    }
  }

  // Guests see broadcast landing; authenticated users with orgs see Home
  return !isAuthenticated ? <LandingPageBroadcast /> : <Home />;
}

/** Syncs auth user and route to Sentry and PostHog. */
function SentryUserSync() {
  const { user } = useAuth();
  const coach = useCoachSession();
  const [location] = useLocation();

  // Admin/Org server-session identity wins over the coach localStorage identity.
  useEffect(() => {
    if (user) {
      setSentryUser(user ?? null);
      setPosthogUser(user ?? null);
      if (user.role === 'COACH') startCoachRecording();
      else stopCoachRecording();
      return;
    }
    if (coach.isAuthenticated && coach.team) {
      const team = coach.team;
      setSentryUser({
        id: team.id,
        email: coach.email ?? undefined,
        role: 'coach',
        teamId: team.id,
        teamName: team.name,
        tournamentId: team.tournamentId,
        organizationId: team.organizationId ?? undefined,
      });
      setCoachIdentity({
        teamId: team.id,
        teamName: team.name,
        tournamentId: team.tournamentId,
        tournamentName: team.tournamentName,
        organizationId: team.organizationId,
        email: coach.email,
      });
      startCoachRecording();
      return;
    }
    setSentryUser(null);
    clearIdentity();
    stopCoachRecording();
  }, [user, coach.isAuthenticated, coach.team, coach.email]);

  useEffect(() => {
    setSentryTags({ page: location || '/' });
  }, [location]);
  return null;
}

/** Fires `pwa_installed` when the browser reports a successful PWA install. */
function PosthogPwaInstallListener() {
  useEffect(() => {
    const handler = () => {
      track('pwa_installed');
    };
    window.addEventListener('appinstalled', handler);
    return () => window.removeEventListener('appinstalled', handler);
  }, []);
  return null;
}

function Router() {
  const [location] = useLocation();
  useWebSocket(pathNeedsLiveSocket(location));

  return (
    <MobileAppShell>
      <Suspense fallback={<LoadingScreen />}>
        <Switch>
          {/* Public routes - always accessible */}
          <Route path="/" component={HostnameAwareHome} />
          <Route path="/broadcast" component={LandingPageBroadcast} />
          <Route path="/directory" component={PublicDirectory} />
          <Route path="/legal/terms" component={TermsOfServicePage} />
          <Route path="/legal/terms-admin" component={AdminTermsOfServicePage} />
          <Route path="/legal/terms-tournament" component={TournamentTermsOfServicePage} />
          <Route path="/legal/privacy" component={PrivacyPolicyPage} />
          <Route path="/legal/algorithm-explanation" component={AlgorithmExplanationPage} />
          <Route path="/org/:slug" component={OrganizationPage} />
          <Route path="/dashboard/:tournamentId" component={Dashboard} />
          <Route path="/tournament/:tournamentId/status" component={TournamentStatusBoardPage} />
          <Route path="/tournament/:tournamentId" component={TournamentDashboard} />
          <Route path="/register/:slug" component={CampaignRegistrationPage} />
          <Route path="/tournaments/:tournamentId/register" component={RegistrationPage} />
          <Route path="/registration/status/:token" component={RegistrationStatusPage} />
          <Route path="/registration/review" component={DirectorApplicationReviewPage} />
          <Route path="/registration/success" component={RegistrationSuccessPage} />
          <Route path="/registration/receipt" component={RegistrationReceiptPage} />
          <Route path="/stay/:stayToken" component={StayPortalPage} />
          <Route path="/league/register" component={LeagueRegistrationPage} />
          <Route path="/roster-entry/:token" component={RosterEntryPage} />
          <Route path="/fit/p/:token" component={FitFormPage} />
          <Route path="/fit/c/:token" component={CoachFitFormPage} />
          <Route path="/coach-score-input/:tournamentId" component={CoachScoreInput} />
          {/* League coach magic link — must be before /coach/:teamHash */}
          <Route path="/coach/league/:token" component={CoachLeaguePage} />
          <Route path="/coach/:teamHash">
            {() => (
              <Suspense fallback={<LoadingScreen />}>
                <CoachTeamView />
              </Suspense>
            )}
          </Route>
          <Route path="/magic" component={MagicLoginPage} />
          <Route path="/coach-dashboard" component={CoachDashboard} />
          <Route path="/staff/leave/:token" component={StaffLeavePage} />
          <Route path="/org/:orgId/staffing">
            {() => <RequireAuth component={StaffingBoardPage} />}
          </Route>
          <Route path="/coach-dashboard-demo" component={CoachDashboardDemo} />
          <Route path="/demo" component={DemoHub} />

          {/* Invitation acceptance - public routes */}
          <Route path="/invite/:token" component={InviteAcceptance} />
          <Route path="/admin-invite/:token" component={AdminInviteAcceptance} />
          <Route path="/team/update/:token" component={TeamUpdate} />

          {/* Onboarding flow for new users without an organization */}
          <Route path="/welcome">{() => <RequireAuth component={WelcomePage} />}</Route>
          <Route path="/onboarding/wizard">
            {() => <RequireAuth component={DirectorOnboardingWizardPage} />}
          </Route>
          <Route path="/onboarding/create-organization">
            {() => <RequireAuth component={OnboardingCreateOrganization} />}
          </Route>
          <Route path="/onboarding/first-tournament">
            {() => <RequireAuth component={FirstTournamentOnboardingPage} />}
          </Route>
          <Route path="/org/:orgId/tournaments/:tournamentId/launch">
            {() => <RequireAuth component={TournamentLaunchPage} />}
          </Route>

          {/* Organization selector - protected */}
          <Route path="/select-organization">
            {() => <RequireAuth component={OrganizationSelector} />}
          </Route>

          {/* Coming Soon pages */}
          <Route
            path="/coming-soon/tournament-registration"
            component={TournamentRegistrationComingSoon}
          />
          <Route path="/coming-soon/tournament-comms" component={TournamentCommsComingSoon} />
          <Route path="/coming-soon/schedule-builder" component={ScheduleBuilderComingSoon} />

          {/* Partner portal. PartnerPortalShell rejects anyone without partnerId. */}
          <Route path="/partner" component={PartnerPortalPage} />

          {/* Platform Admin - Super Admin only, enforced by PlatformAdminShell */}
          <Route path="/platform/integrations" component={PlatformIntegrationsPage} />
          <Route path="/platform/fees" component={PlatformFeesPage} />
          <Route path="/platform/features" component={PlatformFeaturesPage} />
          <Route path="/platform/users" component={PlatformUsersPage} />
          <Route path="/platform/organizations" component={PlatformOrganizationsPage} />
          <Route path="/platform/requests" component={PlatformRequestsPage} />
          <Route path="/platform" component={PlatformDashboardPage} />

          {/* Organization Admin Portal - NEW modular landing page */}
          <Route path="/org/:orgId/admin">{() => <RequireAuth component={OrgAdminPortal} />}</Route>

          {/* User Management - Super Admin Only */}
          <Route path="/org/:orgId/admin/users">
            {() => <RequireAuth component={UserManagement} />}
          </Route>
          <Route path="/org/:orgId/admin/blog/:postId">
            {() => <RequireAuth component={BlogAdminPage} />}
          </Route>
          <Route path="/org/:orgId/admin/blog">
            {() => <RequireAuth component={BlogAdminPage} />}
          </Route>

          {/* New modular feature routes */}
          <Route path="/org/:orgId/communications">
            {() => <RequireAuth component={Communications} />}
          </Route>
          <Route path="/org/:orgId/weather">
            {() => <RequireAuth component={WeatherDashboard} />}
          </Route>
          <Route path="/org/:orgId/teams">
            {() => <RequireAuth component={TeamManagementPage} />}
          </Route>
          <Route path="/org/:orgId/reports">
            {() => <RequireAuth component={BookingReportsPage} />}
          </Route>
          {/* More specific settings routes before /settings */}
          <Route path="/org/:orgId/settings/stripe">
            {() => <RequireAuth component={OrgStripeSettingsPage} />}
          </Route>
          <Route path="/org/:orgId/finance">
            {() => <RequireAuth component={OrgFinanceDeskPage} />}
          </Route>
          <Route path="/org/:orgId/settings">{() => <RequireAuth component={OrgSettings} />}</Route>
          <Route path="/org/:orgId/registrations">
            {() => <RequireAuth component={OrgRegistrationsPage} />}
          </Route>
          <Route path="/org/:orgId/registration-campaigns">
            {() => <RequireAuth component={OrgRegistrationCampaignsPage} />}
          </Route>
          {/* Redirect old booking path to new hub */}
          <Route path="/org/:orgId/booking" component={BookingRedirect} />

          {/* Booking module routes - protected (specific routes first) */}
          <Route path="/booking/:orgId/hub">{() => <RequireAuth component={BookingHub} />}</Route>
          <Route path="/booking/:orgId/calendar">
            {() => <RequireAuth component={BookingCalendarPage} />}
          </Route>
          <Route path="/booking/:orgId/new-request">
            {() => <RequireAuth component={NewBookingRequest} />}
          </Route>
          <Route path="/booking/:orgId/request/:requestId">
            {() => <RequireAuth component={BookingRequestDetail} />}
          </Route>
          <Route path="/booking/:orgId/approvals">
            {() => <RequireAuth component={CoordinatorApprovals} />}
          </Route>
          <Route path="/booking/:orgId/teams">
            {() => <RequireAuth component={TeamManagementPage} />}
          </Route>
          <Route path="/booking/:orgId/reports">
            {() => <RequireAuth component={BookingReportsPage} />}
          </Route>
          <Route path="/booking/:orgId/settings">
            {() => <RequireAuth component={BookingSettingsPage} />}
          </Route>
          <Route path="/booking/:orgId/twilio-settings">
            {() => <RequireAuth component={TwilioSettings} />}
          </Route>
          <Route path="/booking/:orgId/weather-settings">
            {() => <RequireAuth component={WeatherSettings} />}
          </Route>
          <Route path="/booking/:orgId/stripe-settings">
            {() => <RequireAuth component={StripeSettings} />}
          </Route>
          <Route path="/booking/:orgId/communications">
            {() => <RequireAuth component={Communications} />}
          </Route>
          {/* Redirect old booking path to new hub */}
          <Route path="/booking/:orgId" component={BookingRedirect} />

          {/* Protected admin routes - NEW org-scoped routes (canonical) */}
          <Route path="/org/:orgId/tournaments/create">
            {() => <RequireAuth component={TournamentRegistrationShellPage} />}
          </Route>
          <Route path="/org/:orgId/tournaments/:tournamentId/communications">
            {() => <RequireAuth component={TournamentCommunications} />}
          </Route>
          <Route path="/org/:orgId/tournaments/:tournamentId/weather">
            {() => <RequireAuth component={WeatherDashboard} />}
          </Route>
          <Route path="/org/:orgId/tournaments/:tournamentId/validation-report">
            {() => <RequireAuth component={ValidationReport} />}
          </Route>
          <Route path="/org/:orgId/tournaments/:tournamentId">
            {() => <RequireAuth component={AdminPortal} />}
          </Route>

          {/* Legacy admin tournament routes — kept for bookmarks */}
          <Route path="/org/:orgId/tournaments/tournament/:tournamentId/communications">
            {() => <RequireAuth component={TournamentCommunications} />}
          </Route>
          <Route path="/org/:orgId/tournaments/tournament/:tournamentId/weather">
            {() => <RequireAuth component={WeatherDashboard} />}
          </Route>
          <Route path="/org/:orgId/tournaments/tournament/:tournamentId/validation-report">
            {() => <RequireAuth component={ValidationReport} />}
          </Route>
          <Route path="/org/:orgId/tournaments/tournament/:tournamentId">
            {() => <RequireAuth component={AdminPortal} />}
          </Route>
          <Route path="/org/:orgId/tournaments">
            {() => <RequireAuth component={AdminPortal} />}
          </Route>

          {/* League Command Center — Admin index (RequireAuth + isAdmin) */}
          {/* IMPORTANT: Must be defined BEFORE /admin/:tournamentId? to prevent "league" from being treated as a tournament ID */}
          <Route path="/admin/league">
            {() => <RequireAuth component={LeagueCommandCenterWithShell} />}
          </Route>
          <Route path="/admin/league/division/:divisionId">
            {() => <RequireAuth component={LeagueDivisionDetailWithShell} />}
          </Route>
          {/* House League Draft Board — must be before :teamSlug */}
          <Route path="/admin/league/draft/:divisionId">
            {() => <RequireAuth component={LeagueDraftBoardWithShell} />}
          </Route>
          <Route path="/admin/league/master-scheduler">
            {() => <RequireAuth component={LeagueMasterSchedulerWithShell} />}
          </Route>
          <Route path="/admin/league/ai-sandbox">
            {() => <RequireAuth component={LeagueAiSchedulerSandboxWithShell} />}
          </Route>
          <Route path="/admin/league/uniforms">
            {() => <RequireAuth component={LeagueUniformsWithShell} />}
          </Route>
          <Route path="/admin/league/uniform-manifest">
            {() => <RequireAuth component={LeagueUniformManifestWithShell} />}
          </Route>
          <Route path="/admin/league/compliance">
            {() => <RequireAuth component={LeagueComplianceWithShell} />}
          </Route>
          <Route path="/admin/league/financials">
            {() => <RequireAuth component={LeagueFinancialsWithShell} />}
          </Route>
          {/* Staff Manager — Forest Glade RBAC */}
          <Route path="/admin/org/forest-glade/staff">
            {() => <RequireAuth component={ForestGladeStaffPage} />}
          </Route>
          {/* Roster Kiosk — League Management (Forest Glade) */}
          <Route path="/admin/league/:teamSlug">
            {() => <RequireAuth component={RosterKioskWithShell} />}
          </Route>

          {/* Legacy admin routes - kept for backward compatibility */}
          <Route path="/admin/org/:orgId/tournament/:tournamentId/weather">
            {() => <RequireAuth component={WeatherDashboard} />}
          </Route>
          <Route path="/admin/org/:orgId/tournament/:tournamentId/validation-report">
            {() => <RequireAuth component={ValidationReport} />}
          </Route>
          <Route path="/admin/org/:orgId/tournament/:tournamentId">
            {() => <RequireAuth component={AdminPortal} />}
          </Route>
          <Route path="/admin/org/:orgId/booking/calendar">
            {() => <RequireAuth component={AdminBookingCalendarPage} />}
          </Route>
          <Route path="/admin/org/:orgId">{() => <RequireAuth component={AdminPortal} />}</Route>

          {/* Legacy admin routes - kept for backward compatibility, will redirect */}
          <Route path="/admin-portal/:tournamentId?">
            {() => <RequireAuth component={AdminPortal} />}
          </Route>
          <Route path="/admin/tournaments/:tournamentId/dashboard">
            {() => <RequireAuth component={AdminTournamentDashboardRedirect} />}
          </Route>
          <Route path="/admin/booking/calendar">
            {() => <RequireAuth component={AdminBookingCalendarPage} />}
          </Route>
          <Route path="/admin/:tournamentId/validation-report">
            {() => <RequireAuth component={ValidationReport} />}
          </Route>
          <Route path="/admin/:tournamentId?">
            {() => <RequireAuth component={AdminPortal} />}
          </Route>

          {/* Authentication Pages */}
          <Route path="/login" component={LoginPage} />
          <Route path="/register" component={RegisterPage} />
          <Route path="/forgot-password" component={ForgotPasswordPage} />
          <Route path="/reset-password" component={ResetPasswordPage} />
          <Route path="/verify-email" component={VerifyEmailPage} />
          <Route path="/auth/verify" component={VerifyMagicLinkPage} />

          {/* Error Pages */}
          <Route path="/401" component={UnauthorizedPage} />
          <Route path="/500" component={ServerErrorPage} />
          <Route path="/404" component={NotFoundPage} />

          <Route component={NotFoundPage} />
        </Switch>
      </Suspense>
    </MobileAppShell>
  );
}

const swrConfig = {
  // CRITICAL: If the error is 429, DO NOT RETRY. Stop the loop.
  shouldRetryOnError: (err: unknown) => {
    const status =
      (err as { status?: number })?.status ??
      (err as { response?: { status?: number } })?.response?.status;
    if (status === 429) return false;
    return true;
  },
  // Slow down retries for other errors (default is too fast)
  errorRetryInterval: 10000,
  // Stop polling when the window loses focus
  revalidateOnFocus: false,
};

function App() {
  return (
    <SWRConfig value={swrConfig}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <TooltipProvider>
            <Toaster />
            <RateLimitListener />
            <SentryUserSync />
            <PosthogPwaInstallListener />
            <Router />
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </SWRConfig>
  );
}

/** Top-level error boundary: shows actual error in dev, reports to Sentry and PostHog. */
function AppWithErrorBoundary() {
  return (
    <ErrorBoundary
      fallback={(error, errorInfo) => (
        <ErrorFallback error={error} componentStack={errorInfo?.componentStack ?? null} />
      )}
    >
      <App />
    </ErrorBoundary>
  );
}

export default AppWithErrorBoundary;
